import { useSelector } from 'react-redux';
import { RootState } from '../../Store/Store';
import { useAddBundleToCartMutation, useAddSavedSearchToCartMutation, useAddToCartMutation } from '../../Api/Api';
import { useAddBundleToPreparedCartMutation, useAddToPreparedCartMutation, useGetPreparedCartQuery } from '../../Api/PreparedCartEndpoints';
import { ISearchContext } from '../../Api/Models/Products';
import { IBundleSelectionItem } from '../../Api/Models/Cart';
import CAN, { CART_MANAGEMENT } from '../Auth/Permissions';

// The minimal identity needed to add any of the three product kinds to the cart. Serialized into
// a drag payload by the product cards and consumed by the cart drop target. source/searchContext
// carry the origin card's interest-signal context along for the ride.
export interface ICartItemIdentity {
    id?: string;
    catalog?: string;
    savedSearchId?: string;
    bundleId?: string;
    // Default per-item selection for a dragged bundle, so the drop records its line items just as
    // clicking the card's add button would. Omitted for non-bundle items.
    bundleSelection?: IBundleSelectionItem[];
    source?: string;
    searchContext?: ISearchContext;
}

export const CART_DRAG_MIME = 'application/x-ac-cart-item';

// Shared add-to-cart action mirroring the branch logic in ProductCard's add button: routes a
// product / saved-search / bundle to the right (normal or prepared) cart endpoint. Returns whether
// the add was attempted (false when the user lacks cart-management permission). Used by the
// drag-and-drop drop target so a dropped card adds exactly as clicking its add button would.
export function useAddToCartAction() {
    const settings = useSelector((state: RootState) => state.cart);
    const cartAddPermission = CAN(CART_MANAGEMENT);
    const [addToCart] = useAddToCartMutation();
    const [addBundleToCart] = useAddBundleToCartMutation();
    const [addSavedToCart] = useAddSavedSearchToCartMutation();
    const [addToPreparedCart] = useAddToPreparedCartMutation();
    const [addBundleToPreparedCart] = useAddBundleToPreparedCartMutation();
    const getPreparedCart = useGetPreparedCartQuery({ cartId: settings.selectedCartId ?? '' }, { skip: !settings.selectedCartId });

    return async (item: ICartItemIdentity): Promise<boolean> => {
        if (!cartAddPermission) return false;
        const activeCartId = settings.selectedCartId;
        const usePreparedCart = !!activeCartId && (getPreparedCart.data?.canAddAdditionalItems ?? false);
        try {
            if (item.savedSearchId) {
                if (usePreparedCart && item.id && item.catalog) {
                    await addToPreparedCart({ catalog: item.catalog, cartId: activeCartId!, productId: item.id });
                } else {
                    await addSavedToCart({ savedSearchId: item.savedSearchId, source: item.source, searchContext: item.searchContext });
                }
            } else if (item.bundleId) {
                if (usePreparedCart) {
                    await addBundleToPreparedCart({ cartId: activeCartId!, bundleId: item.bundleId, selection: item.bundleSelection });
                } else {
                    await addBundleToCart({ bundleId: item.bundleId, selection: item.bundleSelection, source: item.source, searchContext: item.searchContext });
                }
            } else if (item.id) {
                if (usePreparedCart && item.catalog) {
                    await addToPreparedCart({ catalog: item.catalog, cartId: activeCartId!, productId: item.id });
                } else {
                    await addToCart({ productId: item.id, source: item.source, searchContext: item.searchContext });
                }
            } else {
                return false;
            }
            return true;
        } catch {
            return false;
        }
    };
}
